import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Document } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, PlusCircle, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const DocumentLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedDocuments, setPaginationRelatedDocuments] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedDocument",
    getPaginationRelatedDocuments.pageSize,
    getPaginationRelatedDocuments.page * getPaginationRelatedDocuments.pageSize,
  );

  const { data: countRelatedDocuments } = useCountRelatedFields(id, type, [
    "relatedDocument",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedDocument",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedDocument",
  );

  const [searchContent, setSearchContent] = useState("");

  const handleAddMode = () => {
    // advancedSearch.resetQuery();
    setIsAddMode(!isAddMode);
  };

  const [isAddMode, setIsAddMode] = useState(false);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedDocuments } = useGetNotRelatedResource<Document>({
    resultType: "document",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteRelation(id);
    },
    [deleteRelation],
  );

  const columns: ColumnDef<Document, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "documentsDescriptionNumeroEnregistrement",
        header: t("documentsDescriptionNumeroEnregistrement"),
        size: 50,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.documentsDescriptionNumeroEnregistrement}
            />
          );
        },
      },
      {
        accessorKey: "documentsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"document"}
            />
          );
        },
      },
      {
        accessorKey: "documentsDescriptionTitre",
        header: t("documentsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.documentsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "documentsDescriptionType",
        header: t("documentsDescriptionType"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.documentsDescriptionType} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center justify-end mr-2 gap-1 ">
                <ShowElements
                  type="document"
                  id={row.original.id}
                  elements={[
                    {
                      label: t("documentsDescriptionNumeroEnregistrement"),
                      value:
                        row.original.documentsDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("documentsDescriptionLegendeRedigee"),
                      value: row.original.documentsDescriptionLegendeRedigee,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.attachments,
                      type: "image",
                    },
                  ]}
                  onAdd={() => handleCreateRelation(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelation(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="document"
                id={row.original.id}
                elements={[
                  {
                    label: t("documentsDescriptionNumeroEnregistrement"),
                    value:
                      row.original.documentsDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("documentsDescriptionLegendeRedigee"),
                    value: row.original.documentsDescriptionLegendeRedigee,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [handleCreateRelation, handleDeleteRelation, isAddMode, t]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <h2 className="text-lg font-semibold">{t("linkedDocuments")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <DataTable
        columns={columns}
        data={
          isAddMode ? (notRelatedDocuments?.data ?? []) : (relatedFields ?? [])
        }
        pagination={
          isAddMode
            ? {
                pagination: getPagination,
                setPage: setPagination,
                totalSize: notRelatedDocuments?.totalSize ?? 0,
              }
            : {
                pagination: getPaginationRelatedDocuments,
                setPage: setPaginationRelatedDocuments,
                totalSize: countRelatedDocuments?.relatedDocument,
              }
        }
        options={{
          enableColumnResizing: true,
        }}
      />
    </div>
  );
};

export default DocumentLinks;
